import {
  AdditionalIncomeShape,
  AppShape,
  EmploymentShape,
  IncomeAggregationPayload,
} from "@eave/shared/types/api";
import Big from "big.js";
import {
  AdditionalIncomeTableRow,
  OwnedRealEstateTableRow,
  TaxIncomeSectionTableRow,
} from "./data-helpers";

export interface PaySectionProps {
  dispatch?: Function;
  app: AppShape;
  employment: any;
  summarySection?: string;
  data: any;
  updateAggregation: Function;
}

export interface ModifiedAggregationMap {
  [key: string]: IncomeAggregationPayload;
}

export enum TaxReturnIncomeType {
  Retirement = "retirement",
  CapitalGains = "capital_gains",
  Interest = "interest",
  InstallmentSale = "installment_sale",
  PositiveNetRentalRealEstate = "rental_real_estate",
  Royalty = "royalty",
  Farming = "farming",
  Trust = "trust",

  // Relating to employment
  SoleProprietorship = "sole_proprietorship",
  Partnership = "partnership",
  SCorporation = "s_corporation",
  RegularCorporation = "regular_corporation", // aka "C-Corp"
}

export const TaxReturnIncomeTypeToDisplayText = {
  [TaxReturnIncomeType.Retirement]: "Retirement",
  [TaxReturnIncomeType.CapitalGains]: "Capital Gains",
  [TaxReturnIncomeType.Interest]: "Interest",
  [TaxReturnIncomeType.InstallmentSale]: "Installment Sale",
  [TaxReturnIncomeType.PositiveNetRentalRealEstate]: "Rental Real Estate",
  [TaxReturnIncomeType.Royalty]: "Royalty",
  [TaxReturnIncomeType.Farming]: "Farming",
  [TaxReturnIncomeType.Trust]: "Trust",

  // Relating to employment
  [TaxReturnIncomeType.SoleProprietorship]: "Sole Proprietorship",
  [TaxReturnIncomeType.Partnership]: "Partnership",
  [TaxReturnIncomeType.SCorporation]: "S Corporation",
  [TaxReturnIncomeType.RegularCorporation]: "Regular Corporation", // aka "C-Corp"
};

export enum AdditionalIncomeType {
  ChildSupport = "Child Support",
  Alimony = "Alimony",
  IRADistribution = "IRA Distribution",
  IRA401K = "IRA/401(k)",
  NoteReceivable = "Note Receivable",
  SocialSecurity = "Social Security",
  PensionOrAnnuity = "Pension",
}

export const CALCULATION_METHOD_OPTIONS = [
  {
    label: "Smallest of last 12 months or last 24 months",
    value: "12Or24Months",
  },
  { label: "Last 12 months", value: "12Months" },
  { label: "Last 24 months", value: "24Months" },
];

// if qualification_year has changed since year_1_override was set,
// year_1_override may be invalid (ie not qualification_year - 1 or qualification_year - 2),
// in which case display the current value, and Ops can just
// select a different, valid one in the dropdown.
export const taxReturnYearOptions = (qualification_year, year1) => {
  let year1Options = [qualification_year - 1, qualification_year - 2];
  if (!year1Options.includes(year1)) {
    year1Options = [year1, ...year1Options];
  }

  return year1Options.map(o => ({
    text: `${o}`,
    value: o,
  }));
};

// Only considers "salaried" income -- not that from tax returns. A special case.
export const calculateSumForEmployment = (employment: EmploymentShape): number => {
  return employment.income_aggregations.reduce((acc, aggregation) => {
    if (aggregation.include_in_ratios) {
      return acc + Number(aggregation.monthlyIncomeMap[aggregation.calculation_method]);
    }
    return acc;
  }, 0);
};

export const calculateSumForEmployments = (employments: EmploymentShape[]): number => {
  return employments.reduce((acc, emp) => acc + calculateSumForEmployment(emp), 0);
};

export const calculateMonthlyIncomeForEmployments = (employments: EmploymentShape[]): Big => {
  return employments.reduce(
    (acc: Big, emp: EmploymentShape) => acc.add(emp.monthly_income),
    Big(0),
  );
};

export type IncomeTableRow =
  | AdditionalIncomeTableRow
  | TaxIncomeSectionTableRow
  | OwnedRealEstateTableRow;

export const calculateMonthlyIncomeForOtherIncomes = (otherIncomes: IncomeTableRow[]) => {
  return otherIncomes
    .filter(i => !i.excludeFromTotal)
    .reduce((acc: Big, income) => acc.add(parseFloat(income.monthly_total) || 0), Big(0));
};
